import { type Language } from "@ap/i18n";
import { PageTermsContext, TermLinkContext, useAuth, useColorMode } from "@ap/ui-kit";
import { Drawer } from "antd";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { useFocusFromUrl } from "../lib/focusField";
import { useNeedsYou } from "../lib/needsYou";
import { readPref, writePref } from "../lib/storage";
import { routeOf } from "../routes";
import { CommandPalette } from "./CommandPalette";
import { HelpDrawer } from "./HelpDrawer";
import { Rail } from "./Rail";
import { ShellUiContext, type ShellUi } from "./shellUi";
import { TopBar } from "./TopBar";
import { markTourSeen, shouldAutoStartTour, Tour, tourSteps, type TourStep } from "./Tour";

export const RAIL_KEY = "ap.rail";
/** Below this width the rail becomes a top bar with a menu drawer. */
export const NARROW_QUERY = "(max-width: 899px)";

function useNarrow(): boolean {
  const get = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(NARROW_QUERY).matches;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(NARROW_QUERY);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return narrow;
}

/**
 * The console shell (ADR-0055): collapsible icon rail (top bar + drawer on narrow screens), top bar
 * with page context, command palette, "Needs you" bell, help and language; help drawer and the
 * first-run tour.
 */
export function AppLayout({ lang, onLanguage, children }: { lang: Language; onLanguage: (l: Language) => void; children: ReactNode }) {
  const { t } = useTranslation("shell");
  const { user } = useAuth();
  const { toggle } = useColorMode();
  const nav = useNavigate();
  const loc = useLocation();
  useFocusFromUrl();
  const narrow = useNarrow();
  const [collapsed, setCollapsed] = useState(() => readPref(RAIL_KEY) === "collapsed");
  const [drawer, setDrawer] = useState(false);
  const [palette, setPalette] = useState(false);
  const [help, setHelp] = useState(false);
  const [tour, setTour] = useState<TourStep[] | null>(null);
  const { needs, total } = useNeedsYou(user);
  const route = routeOf(loc.pathname);

  const startTour = useCallback(() => setTour(tourSteps(user?.roles ?? [], location.pathname)), [user]);
  const endTour = useCallback(() => {
    setTour(null);
    markTourSeen(user?.username ?? "");
  }, [user]);

  // First run: once per user and tour version, after the page has had a moment to render.
  useEffect(() => {
    if (!user || !shouldAutoStartTour(user.username)) return;
    const id = setTimeout(startTour, 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);

  // Ctrl/⌘ + K opens the command palette from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => setDrawer(false), [loc.pathname]);

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      writePref(RAIL_KEY, c ? null : "collapsed");
      return !c;
    });

  const ui: ShellUi = useMemo(
    () => ({ openPalette: () => setPalette(true), openHelp: () => setHelp(true), startTour, needs, needsTotal: total }),
    [startTour, needs, total],
  );
  const openGlossary = useCallback((id: string) => nav(`/glossary#${encodeURIComponent(id)}`), [nav]);
  const paletteCtx = useMemo(() => ({ navigate: (p: string) => nav(p), startTour, toggleTheme: toggle }), [nav, startTour, toggle]);
  const terms = useMemo(() => route?.terms ?? [], [route]);

  return (
    <ShellUiContext.Provider value={ui}>
      <TermLinkContext.Provider value={openGlossary}>
        <div className="ap-shell" data-collapsed={!narrow && collapsed ? "true" : "false"} data-narrow={narrow ? "true" : "false"}>
          <a className="skip-link" href="#main">
            {t("skip")}
          </a>
          {!narrow ? (
            <aside className="ap-rail" data-testid="rail">
              <Rail lang={lang} variant="rail" collapsed={collapsed} onToggleCollapsed={toggleCollapsed} onHelp={() => setHelp(true)} />
            </aside>
          ) : (
            <Drawer
              open={drawer}
              onClose={() => setDrawer(false)}
              placement={lang === "ar" ? "right" : "left"}
              width={280}
              closable={false}
              className="ap-rail-drawer"
              styles={{ body: { padding: 0 } }}
              title={<span className="ap-visually-hidden">{t("navLabel")}</span>}
            >
              <Rail lang={lang} variant="drawer" collapsed={false} onNavigate={() => setDrawer(false)} onHelp={() => setHelp(true)} />
            </Drawer>
          )}
          <div className="ap-main-col">
            <TopBar lang={lang} onLanguage={onLanguage} compact={narrow} onMenu={() => setDrawer(true)} />
            <main id="main" tabIndex={-1} className="ap-main">
              <div className="ap-container" data-route={route?.key}>
                <PageTermsContext.Provider value={terms}>{children}</PageTermsContext.Provider>
              </div>
            </main>
          </div>
        </div>
        <CommandPalette open={palette} onClose={() => setPalette(false)} ctx={paletteCtx} />
        <HelpDrawer open={help} onClose={() => setHelp(false)} lang={lang} onTour={startTour} />
        <Tour open={tour !== null} steps={tour ?? []} lang={lang} onClose={endTour} />
      </TermLinkContext.Provider>
    </ShellUiContext.Provider>
  );
}
